/**
 * 34. Intercompany & Consolidation — mapping rules, eliminations,
 * currency translation, consolidation run.
 *
 * Arabic and English (C7): every word from the catalogs; statuses are words,
 * the last run a date and time the locale writes.
 */
import { Card, PageHeader, DataTable, Skeleton, Badge } from "@/components/ui";
import { PendingButton } from "@/components/PendingAction";
import { useApiList } from "@/lib/api/hooks";
import { useTranslation } from "@/lib/i18n";
import { statusLabel } from "@/lib/i18n/records";
import { formatDateTime, formatMoney, formatQuantity } from "@/lib/money";

interface ConsolidationGroup {
  id: string;
  name: string;
  entities: number;
  lastRunAt: string | null;
  status: "NotRun" | "Completed" | "Failed";
}

interface OpenItem {
  id: string;
  counterparty: string;
  documentNumber: string;
  amount: string;
  currencyCode: string;
  status: "Open" | "Matched" | "Settled";
}

export function ConsolidationPage() {
  const { t } = useTranslation();
  const { data: groups, isLoading } = useApiList<ConsolidationGroup>(
    ["finance", "consolidation-groups"],
    "/finance/consolidation/groups",
  );
  const { data: openItems } = useApiList<OpenItem>(
    ["finance", "intercompany-open"],
    "/finance/intercompany/open-items",
  );

  const openCount = (openItems?.items ?? []).filter(
    (i) => i.status === "Open",
  ).length;

  return (
    <div>
      <PageHeader
        title={t("finance.consolidation.title")}
        subtitle={t("finance.consolidation.subtitle")}
        actions={
          <PendingButton variant="primary" reason="afterLaunch" slice="F22">
            {t("finance.consolidation.run")}
          </PendingButton>
        }
      />
      {openCount > 0 && (
        <Card
          title={t("finance.consolidation.openItems")}
          actions={
            <Badge tone="danger">
              {t("finance.consolidation.openCount", { count: openCount })}
            </Badge>
          }
        >
          <DataTable
            rows={openItems?.items ?? []}
            columns={[
              {
                key: "counterparty",
                label: t("finance.consolidation.counterparty"),
              },
              {
                key: "documentNumber",
                label: t("finance.consolidation.document"),
              },
              {
                key: "amount",
                label: t("fields.amount"),
                numeric: true,
                render: (r) => formatMoney(r.amount, r.currencyCode),
              },
              {
                key: "status",
                label: t("fields.status"),
                render: (r) => (
                  <Badge
                    tone={
                      r.status === "Open"
                        ? "warning"
                        : r.status === "Settled"
                          ? "success"
                          : "primary"
                    }
                  >
                    {statusLabel(t, "IntercompanyItem", r.status)}
                  </Badge>
                ),
              },
            ]}
          />
        </Card>
      )}
      {isLoading || !groups ? (
        <Skeleton rows={6} />
      ) : (
        <Card title={t("finance.consolidation.groups")}>
          <DataTable<ConsolidationGroup>
            rows={groups.items}
            columns={[
              { key: "name", label: t("fields.name") },
              {
                key: "entities",
                label: t("finance.consolidation.entities"),
                numeric: true,
                render: (r) => formatQuantity(r.entities, 0),
              },
              {
                key: "lastRunAt",
                label: t("finance.consolidation.lastRun"),
                render: (r) => formatDateTime(r.lastRunAt),
              },
              {
                key: "status",
                label: t("fields.status"),
                render: (r) =>
                  r.status ? (
                    <Badge
                      tone={
                        r.status === "Completed"
                          ? "success"
                          : r.status === "Failed"
                            ? "danger"
                            : "default"
                      }
                    >
                      {statusLabel(t, "ConsolidationGroup", r.status)}
                    </Badge>
                  ) : (
                    "—"
                  ),
              },
            ]}
          />
        </Card>
      )}
    </div>
  );
}
